import React, { useState } from 'react';
import { Card, Drawer, Select, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export interface EpcEstimateCostExplorerPanelProps {
  estimateId: string;
  revisionId: string;
  onSelectBoqBucket?: (boqLineId: string) => void;
}

/**
 * EST3 UX3 — Cost explorer with BOQ drill-down.
 */
export const EpcEstimateCostExplorerPanel: React.FC<EpcEstimateCostExplorerPanelProps> = ({
  estimateId,
  revisionId,
  onSelectBoqBucket,
}) => {
  const [view, setView] = useState<'wbs' | 'cbs' | 'client_boq'>('wbs');
  const [drillKey, setDrillKey] = useState<string | null>(null);

  const explorerQ = useQuery({
    queryKey: ['epc-est-explorer', estimateId, revisionId, view],
    queryFn: () => epcEstimationService.costExplorer(estimateId, revisionId, view),
    enabled: !!estimateId && !!revisionId,
  });

  const drillQ = useQuery({
    queryKey: ['epc-est-cost-drill', estimateId, revisionId, drillKey],
    queryFn: () =>
      epcEstimationService.listCostLines(estimateId, revisionId, {
        clientBoqLineId: drillKey!,
      }),
    enabled: !!estimateId && !!revisionId && !!drillKey && view === 'client_boq',
  });

  const total = explorerQ.data?.total_amount;
  const isBoq = view === 'client_boq';

  return (
    <Card size="small" title="Cost Explorer">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <Select
            value={view}
            onChange={(v) => {
              setView(v);
              setDrillKey(null);
            }}
            style={{ width: 180 }}
            options={[
              { value: 'wbs', label: 'By WBS' },
              { value: 'cbs', label: 'By CBS' },
              { value: 'client_boq', label: 'By Client BOQ' },
            ]}
          />
          <Tag color="blue">
            Total {formatMoney(total ?? 0, { display: 'plain', maximumFractionDigits: 0 })}
          </Tag>
          <Text type="secondary">{explorerQ.data?.buckets?.length ?? 0} bucket(s)</Text>
        </Space>
        <Table
          size="small"
          rowKey="key"
          loading={explorerQ.isLoading}
          dataSource={explorerQ.data?.buckets || []}
          pagination={(explorerQ.data?.buckets?.length || 0) > 50 ? { pageSize: 50 } : false}
          scroll={{ x: true }}
          onRow={(row: {
            key?: string;
            code?: string;
            name?: string;
          }) => ({
            onClick: () => {
              if (isBoq && row.key && row.key !== '_unallocated') {
                setDrillKey(row.key);
                onSelectBoqBucket?.(row.key);
              }
            },
            style: {
              cursor: isBoq && row.key !== '_unallocated' ? 'pointer' : undefined,
            },
          })}
          columns={[
            { title: 'Code', dataIndex: 'code', width: 100 },
            { title: 'Name', dataIndex: 'name', ellipsis: true },
            { title: 'Lines', dataIndex: 'line_count', width: 80 },
            {
              title: 'Cost total',
              dataIndex: 'total_amount',
              width: 120,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
            },
            ...(isBoq
              ? [
                  {
                    title: 'Sell',
                    dataIndex: 'sell_amount',
                    width: 100,
                    render: (v: number) =>
                      formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
                  },
                  {
                    title: 'Margin',
                    dataIndex: 'margin_pct',
                    width: 72,
                    render: (v: number | null) => (v != null ? `${v}%` : '—'),
                  },
                ]
              : []),
          ]}
        />
      </Space>

      <Drawer
        title="Linked cost lines"
        open={!!drillKey}
        onClose={() => setDrillKey(null)}
        width={520}
      >
        <Table
          size="small"
          rowKey="id"
          loading={drillQ.isLoading}
          dataSource={drillQ.data || []}
          pagination={false}
          columns={[
            { title: 'Description', dataIndex: 'description', ellipsis: true },
            { title: 'Kind', dataIndex: 'resource_kind', width: 90 },
            {
              title: 'Total',
              dataIndex: 'total_amount',
              width: 100,
              render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 0 }),
            },
          ]}
        />
      </Drawer>
    </Card>
  );
};
